<template>
  <div class="product">
    <div v-if="loadingProduct" class="product__loading">
      <LoadingDots />
    </div>
    <ProductFetchError v-if="!loadingProduct && productFetchError" />
    <ProductMeta v-if="productWasFetchedCorrectly" :product="product" />
    <ProductGallery
      v-if="productWasFetchedCorrectly"
      :product="product"
      ref="productGalleryComponent"
    />
    <div class="product__info" v-if="productWasFetchedCorrectly">
      <ProductBreadcrumbs :product="product" />
      <ProductTitle
        :product="product"
        :is-in-wishlist="isInWishlist"
        @addToWishlist="
          isInWishlist
            ? removeItemFromWishlist({ product, quantity: 1 })
            : addProductToWishlist({ product, quantity: 1 })
        "
      />
      <ProductPrice :product="product" v-if="!isFirstVariantLoading" />
      <div v-else class="product__info__dots">
        <LoadingDots></LoadingDots>
      </div>
      <ProductConfigurator
        :product="product"
        @updateProductConfiguration="(l, v) => updateProductConfiguration(l, v)"
        :product-configuration="productConfiguration"
        :default-variant="getDefaultVariant()"
        v-if="!isFirstVariantLoading"
      />
      <SocialShareBarDesktop :url="pageUrl" :quote="shareQuote" />
      <SocialShareBarMobile :url="pageUrl" :quote="shareQuote" />
    </div>
    <ProductDetails
      v-if="productWasFetchedCorrectly"
      :product="product"
      :features="blocksData && blocksData.features ? blocksData.features : []"
    />
    <template v-if="blocksData && blocksData.pairedMedia">
      <PairedMedia
        v-for="(pairedMediaItem, index) in blocksData.pairedMedia"
        :key="`paired-media-${index}`"
        :media="pairedMediaItem"
      />
    </template>
    <template v-if="blocksData && blocksData.designers">
      <ImageWithText
        v-for="(designer, index) in blocksData.designers"
        :key="`designer-${index}`"
        :data="designer"
        :isDesigner="true"
      />
    </template>
    <HandCraftedBlock
      v-if="
        !loadingProduct &&
        blocksData &&
        blocksData.handcrafted &&
        blocksData.handcrafted.active
      "
      :handcrafted-data="blocksData.handcrafted.data"
      :videoInsteadOfImage="true"
    />
    <FaqSection
      v-if="blocksData && blocksData.faq && blocksData.faq.active"
      :questions="blocksData.faq.data"
    />
    <ImageWithText
      v-if="
        blocksData &&
        blocksData.customisedGlassLighting &&
        blocksData.customisedGlassLighting.active
      "
      :data="blocksData.customisedGlassLighting.data"
    />
    <ProductsGrid
      v-if="productWasFetchedCorrectly"
      :title="$t('Suggested for you')"
      layout="row"
      :products="product.related_products"
      class="product__products-grid"
      grid-id="pdp-related"
    />
    <ProductsGrid
      v-if="latestViewProducts && !loadingProductsList"
      :title="$t('Latest View')"
      layout="row"
      :products="latestViewProducts"
      class="product__products-grid"
      grid-id="pdp-latest"
    />
    <NeedHelp />
    <StructuredBreadcrumbData />
    <StructuredProductData
      v-if="productWasFetchedCorrectly"
      :product="product"
    />
  </div>
</template>

<script>
import {
  productGetters,
  useProduct,
  wishlistGetters,
} from '@gemini-vsf/composables';
import {
  computed,
  defineComponent,
  onMounted,
  ref,
  useContext,
  useFetch,
  useMeta,
  useRoute,
} from '@nuxtjs/composition-api';
import { SfBreadcrumbs } from '@storefront-ui/vue';
import { CacheTagPrefix, useCache } from '@gemini/vsf-cache';
import cacheControl from '~/helpers/cacheControl';
import { useGtm, useWindow, useWishlist } from '~/composables';
import HandCraftedBlock from '~/components/Home/HandCraftedBlock.vue';
import LazyHydrate from 'vue-lazy-hydration';
import {
  ProductBreadcrumbs,
  ProductConfigurator,
  ProductDetails,
  ProductFetchError,
  ProductGallery,
  ProductMeta,
  ProductPrice,
  ProductTitle,
} from '~/components/Catalog/Product';
import { ProductsGrid } from '~/components/Catalog';
import { useProductSearch } from '~/composables/catalog';
import {
  FaqSection,
  ImageWithText,
  LoadingDots,
  NeedHelp,
  PairedMedia,
} from '~/components/General';
import productBlocks from '~/helpers/product/productBlocks';
import SocialShareBarMobile from '~/components/SocialShareBar/SocialShareBarMobile';
import SocialShareBarDesktop from '~/components/SocialShareBar/SocialShareBarDesktop';
import useGeminiApi from '~/composables/useGeminiApi/useGeminiApi';
import { useGetAlternateMatching } from '~/helpers/alternate/getAlternateMatching';
import { handleSeo } from '~/helpers/seo/handleSeo';
import StructuredBreadcrumbData from '~/components/StructuredData/StructuredBreadcrumbData.vue';
import StructuredProductData from '~/components/StructuredData/StructuredProductData.vue';

export default defineComponent({
  name: 'ProductPage',
  components: {
    SocialShareBarMobile,
    SocialShareBarDesktop,
    ProductConfigurator,
    ProductDetails,
    SfBreadcrumbs,
    HandCraftedBlock,
    LazyHydrate,
    ProductMeta,
    ProductGallery,
    PairedMedia,
    NeedHelp,
    ImageWithText,
    LoadingDots,
    FaqSection,
    ProductPrice,
    ProductBreadcrumbs,
    ProductTitle,
    ProductFetchError,
    ProductsGrid,
    StructuredBreadcrumbData,
    StructuredProductData,
  },
  middleware: cacheControl({
    'max-age': 60,
    'stale-when-revalidate': 5,
  }),
  scrollToTop: false,
  transition: 'fade',
  props: {
    productId: {
      type: [String],
      default: '',
    },
  },
  head() {
    return {
      title: this.metaTitle,
    };
  },
  setup(props, { refs }) {
    const route = useRoute();
    const id = props?.productId || route?.value?.params?.productId || '';
    const { pageGtmPush, productGtmPush, addToWishlistGtmPush } = useGtm();
    const isFirstVariantLoading = ref(true);
    const {
      app: {
        i18n: {
          localeProperties: { localeValue },
        },
      },
      error: nuxtError,
    } = useContext();
    const {
      search: getProductsList,
      loading: loadingProductsList,
      products: productsList,
    } = useProduct(`Product-${id}`);
    const {
      getProductById,
      getProductWithVariant,
      product,
      error: { search: productFetchError },
      getSpecificVariant,
    } = useProductSearch();
    const loadingProduct = ref(true);
    const productWasFetchedCorrectly = computed(
      () =>
        product.value &&
        Object.entries(product.value).length > 0 &&
        !loadingProduct.value &&
        !productFetchError
    );

    const seoAlternateUrls = ref([]);
    const alternate = computed(() => {
      return seoAlternateUrls.value;
    });
    const { getGeminiData, geminiData } = useGeminiApi();
    const { getAlternateArray } = useGetAlternateMatching();
    const { getCanonical } = handleSeo();
    const canonicalLink = computed(() => getCanonical());

    const getGeminiDataFunction = async () => {
      await getGeminiData(`grn:product:product::${id}`);
      seoAlternateUrls.value = getAlternateArray(geminiData.value);
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return
      return JSON.parse(JSON.stringify(seoAlternateUrls.value));
    };

    const { realWindowHeight } = useWindow();

    const { addTags } = useCache();

    const productConfiguration = ref();

    const latestViewProducts = ref();

    const currentProductSelectedVariantData = computed(
      () => product.value?.configurable_product_options_selection?.variant
    );

    const { getPdpBlocks } = productBlocks();
    const blocksData = ref();
    const fetchBlockContent = async () => {
      blocksData.value = await getPdpBlocks(product.value, localeValue);
    };

    const {
      itemsInWishlist,
      loadItemsFromList,
      removeItemFromWishlist,
      addItemToWishlist,
      wishlist,
    } = useWishlist();

    const addProductToWishlist = async (product, quantity) => {
      await addItemToWishlist(product, quantity);
      addToWishlistGtmPush(product, currentProductSelectedVariantData.value);
    };

    const currentVariant = computed(
      () =>
        product.value?.configurable_product_options_selection?.variant?.sku ||
        product.value?.sku
    );

    const isInWishlist = computed(() => {
      const wishlistItems = wishlistGetters.getItems(wishlist.value);
      const item = wishlistItems.find((p) => {
        return p.item_grn === currentVariant.value;
      });
      return item !== undefined;
    });

    const getDefaultVariant = () => {
      let defaultVariant = product?.value?.variants?.find(
        (v) => v?.product?.merchant_sku === route.value?.query?.variant_sku
      )?.option_uids;
      if (!defaultVariant) {
        defaultVariant = product?.value?.variants?.find(
          (v) =>
            v?.product?.merchant_sku === product?.value?.sdc_default_combination
        )?.option_uids;
      }
      if (!defaultVariant) {
        defaultVariant = product?.value?.variants?.find(
          (v) => v?.product?.status
        )?.option_uids;
      }
      return defaultVariant;
    };

    const fetchProduct = async () => {
      await getProductById(id, productConfiguration, 'productDetailCustom');
      if (product.value) {
        const defaultVariant = getDefaultVariant();
        if (defaultVariant) {
          await getSpecificVariant(id, defaultVariant, 'productDetailCustom');
          productConfiguration.value = defaultVariant;
        }
        await loadItemsFromList([product.value]);
        addTags([
          { prefix: CacheTagPrefix.View, value: 'product' },
          { prefix: CacheTagPrefix.Product, value: id },
        ]);
      } else {
        return nuxtError({ statusCode: 404 });
      }
      loadingProduct.value = false;
      isFirstVariantLoading.value = false;
      await fetchBlockContent();
    };

    const updateProductConfiguration = async (label, value) => {
      productConfiguration.value = await getProductWithVariant(
        id,
        label,
        value,
        productConfiguration.value,
        'productDetailCustom'
      );
      refs?.productGalleryComponent?.resetIndex();
      productGtmPush(product.value);
    };

    const fetchLatestViewProducts = async () => {
      await getProductsList({
        queryType: 'LIST',
        pageSize: 8,
        currentPage: 1,
        sort: {
          creation: 'DESC',
        },
        customQuery: {
          products: 'productListCustom',
        },
      });
      if (productsList.value?.items?.length) {
        latestViewProducts.value = productsList.value.items.filter(Boolean);
      }
    };

    useFetch(async () => {
      await fetchProduct();
      await fetchLatestViewProducts();
      await getGeminiDataFunction();
    });

    const gtmDataSent = ref(false);
    const sendGtmData = () => {
      if (!gtmDataSent.value) {
        if (product.value) {
          productGtmPush(product.value);
          pageGtmPush(
            product.value?.name || '',
            productGetters.getProductMerchantSku(product.value) ?? null
          );
          gtmDataSent.value = true;
        } else {
          setTimeout(() => sendGtmData(), 1000); // eslint-disable-line unicorn/numeric-separators-style
        }
      }
    };
    onMounted(() => {
      sendGtmData();
    });

    const productThumbnail = computed(() =>
      productGetters.getProductThumbnailImage(product.value)
    );
    useMeta(() => ({
      meta: [
        {
          hid: 'og:image',
          property: 'og:image',
          content: productThumbnail.value,
        },
        {
          hid: 'og:image:width',
          property: 'og:image:width',
          content: '650',
        },
        // og:url
        {
          hid: 'og:url',
          property: 'og:url',
          content: canonicalLink.value?.href,
        },
        { hid: 'fb:app_id', property: 'fb:app_id', content: '208478719244524' },
      ],
      link: [...alternate.value, canonicalLink.value],
    }));

    const pageUrl = computed(() =>
      typeof window === 'undefined'
        ? 'www.sognidicristallo.com'
        : window.location.href
    );
    const shareQuote = computed(() => product.value?.name || '');

    return {
      product,
      shareQuote,
      pageUrl,
      blocksData,
      productWasFetchedCorrectly,
      realWindowHeight,
      loadingProduct,
      productConfiguration,
      productFetchError,
      latestViewProducts,
      loadingProductsList,
      itemsInWishlist,
      isInWishlist,
      isFirstVariantLoading,
      addProductToWishlist,
      removeItemFromWishlist,
      updateProductConfiguration,
      getDefaultVariant,
      seoAlternateUrls,
      alternate,
    };
  },
});
</script>

<style lang="scss">
.product {
  width: 100vw;
  max-width: 120rem;
  display: flex;
  flex-direction: column;
  &__info {
    box-sizing: border-box;
    width: 100%;
    padding: 1.8125rem 1.25rem;
    display: flex;
    flex-direction: column;
    &__dots {
      .dot-flashing {
        margin: 0 100% 0 2.5%;
      }
    }
  }
  &__loading {
    min-height: 100vh;
    min-height: var(--real-window-height, 100vh);
    width: 100vw;
  }
  &__products-grid {
    margin: 2.5rem 1.25rem;
  }
  @include from-desktop-min {
    flex-direction: row;
    flex-wrap: wrap;
    &__info {
      width: 50%;
      padding: 3.75rem 8.1875rem 4.875rem 8.3125rem;
    }
    &__products-grid {
      margin: 5rem 1.25rem;
      width: inherit;
    }
  }
  @include from-desktop-min {
    #hand-crafted-by {
      .image-block {
        .hp-video {
          &__player {
            padding: 79% 0 0 0 !important;
          }
        }
      }
    }
  }
}
</style>
